/**
 * ProcessosPage.tsx — hub "Geração de Processo Licitatório" (F5).
 * Réplica do templates/processo_licitatorio.html: header com ícone
 * gradiente e estatísticas, timeline das 7 etapas, card de novo processo,
 * e grid de cards com barra de progresso, trilhos por etapa e as ações
 * contextuais (próxima ação da cadeia vem calculada pelo backend).
 */

import {
  faCalendarAlt,
  faCheckCircle,
  faClipboardCheck,
  faClipboardList,
  faClock,
  faEdit,
  faExclamationTriangle,
  faFileAlt,
  faFileContract,
  faFileSignature,
  faFolderOpen,
  faHandshake,
  faHandPointer,
  faPlus,
  faPlusCircle,
  faRoute,
  faScroll,
  faSearch,
  faSpinner,
  faStream,
  faTrashAlt,
  type IconDefinition,
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useMemo, useState } from 'react'
import {
  excluirProcessoLicitatorio,
  listarProcessosLicitatorios,
  type Acao,
  type ProcessoLicitatorio,
} from '../../api/processos'
import { useToast } from '../../components/Toast'

// As 7 etapas do fluxo, com as cores do template original
const ETAPAS: {
  chave: string
  rotulo: string
  curto: string
  descricao: string
  icone: IconDefinition
  gradiente: string
  brilho: string
  texto: string
  trilho: string
}[] = [
  {
    chave: 'dfd', rotulo: 'DFD', curto: 'DFD', descricao: 'Formalização',
    icone: faFileAlt, gradiente: 'from-emerald-500 to-green-600',
    brilho: 'shadow-emerald-500/30', texto: 'text-emerald-600 dark:text-emerald-400',
    trilho: 'bg-emerald-500',
  },
  {
    chave: 'etp', rotulo: 'ETP', curto: 'ETP', descricao: 'Estudo Técnico',
    icone: faClipboardList, gradiente: 'from-amber-500 to-yellow-600',
    brilho: 'shadow-amber-500/30', texto: 'text-amber-600 dark:text-amber-400',
    trilho: 'bg-amber-500',
  },
  {
    chave: 'tr', rotulo: 'TR', curto: 'TR', descricao: 'Termo de Ref.',
    icone: faFileSignature, gradiente: 'from-blue-500 to-cyan-600',
    brilho: 'shadow-blue-500/30', texto: 'text-blue-600 dark:text-blue-400',
    trilho: 'bg-blue-500',
  },
  {
    chave: 'edital', rotulo: 'Edital', curto: 'Edit.', descricao: 'Licitação',
    icone: faScroll, gradiente: 'from-purple-500 to-violet-600',
    brilho: 'shadow-purple-500/30', texto: 'text-purple-600 dark:text-purple-400',
    trilho: 'bg-purple-500',
  },
  {
    chave: 'minuta', rotulo: 'Minuta', curto: 'Min.', descricao: 'Contrato',
    icone: faFileContract, gradiente: 'from-orange-500 to-amber-600',
    brilho: 'shadow-orange-500/30', texto: 'text-orange-600 dark:text-orange-400',
    trilho: 'bg-orange-500',
  },
  {
    chave: 'arp', rotulo: 'ARP', curto: 'ARP', descricao: 'Reg. Preços',
    icone: faClipboardCheck, gradiente: 'from-pink-500 to-rose-600',
    brilho: 'shadow-pink-500/30', texto: 'text-pink-600 dark:text-pink-400',
    trilho: 'bg-pink-500',
  },
  {
    chave: 'contrato', rotulo: 'Contrato', curto: 'Contr.', descricao: 'Administrativo',
    icone: faHandshake, gradiente: 'from-indigo-500 to-blue-600',
    brilho: 'shadow-indigo-500/30', texto: 'text-indigo-600 dark:text-indigo-400',
    trilho: 'bg-indigo-500',
  },
]

const TONS_ACAO: Record<Acao['tom'], string> = {
  esmeralda: 'from-emerald-500 to-green-600 hover:from-emerald-600 hover:to-green-700 shadow-emerald-500/30',
  ambar: 'from-amber-500 to-yellow-600 hover:from-amber-600 hover:to-yellow-700 shadow-amber-500/30',
  azul: 'from-blue-500 to-cyan-600 hover:from-blue-600 hover:to-cyan-700 shadow-blue-500/30',
  roxo: 'from-purple-500 to-violet-600 hover:from-purple-600 hover:to-violet-700 shadow-purple-500/30',
  laranja: 'from-orange-500 to-amber-600 hover:from-orange-600 hover:to-amber-700 shadow-orange-500/30',
}

function Cabecalho({ processos }: { processos: ProcessoLicitatorio[] }) {
  const completos = processos.filter((p) => p.etapas.tr).length
  return (
    <div className="mb-8 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
      <div>
        <div className="mb-2 flex items-center gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 shadow-lg shadow-indigo-500/30">
            <FontAwesomeIcon icon={faFileContract} className="text-xl text-white" />
          </div>
          <h1 className="bg-gradient-to-r from-slate-800 to-slate-600 bg-clip-text text-2xl font-bold text-transparent dark:from-slate-100 dark:to-slate-300 md:text-3xl">
            Geração de Processo Licitatório
          </h1>
        </div>
        <p className="flex flex-wrap items-center gap-2 text-slate-500 dark:text-slate-400">
          <FontAwesomeIcon icon={faRoute} className="text-indigo-500" />
          Fluxo inteligente conforme Lei 14.133/2021
          <span className="rounded-full bg-indigo-100 px-2 py-0.5 text-xs font-medium text-indigo-600 dark:bg-indigo-900/30 dark:text-indigo-400">
            Nova Lei de Licitações
          </span>
        </p>
      </div>
      <div className="flex items-center gap-3">
        {[
          { rotulo: 'Total', valor: processos.length, cor: 'text-indigo-600 dark:text-indigo-400' },
          { rotulo: 'Em Andamento', valor: processos.length - completos, cor: 'text-amber-600 dark:text-amber-400' },
          { rotulo: 'Completos', valor: completos, cor: 'text-emerald-600 dark:text-emerald-400' },
        ].map((e) => (
          <div
            key={e.rotulo}
            className="rounded-xl border border-slate-200 bg-white px-4 py-2 shadow-sm dark:border-slate-700 dark:bg-dark-card"
          >
            <div className="text-xs text-slate-500 dark:text-slate-400">{e.rotulo}</div>
            <div className={`text-xl font-bold ${e.cor}`}>{e.valor}</div>
          </div>
        ))}
      </div>
    </div>
  )
}

function FluxoDoProcesso() {
  return (
    <div className="mb-8 overflow-hidden rounded-2xl border border-slate-200 bg-white p-6 shadow-lg dark:border-slate-700 dark:bg-dark-card">
      <div className="mb-6 flex items-center gap-2">
        <FontAwesomeIcon icon={faStream} className="text-indigo-500" />
        <h2 className="text-lg font-bold text-slate-800 dark:text-slate-100">
          Fluxo do Processo Licitatório
        </h2>
      </div>
      <div className="relative">
        <div className="absolute left-0 right-0 top-8 hidden h-1 rounded-full bg-gradient-to-r from-emerald-200 via-blue-200 to-purple-200 dark:from-emerald-800 dark:via-blue-800 dark:to-purple-800 md:block" />
        <div className="relative grid grid-cols-2 gap-4 md:grid-cols-7 md:gap-2">
          {ETAPAS.map((etapa, i) => (
            <div key={etapa.chave} className="group flex flex-col items-center">
              <div
                className={`z-10 flex h-16 w-16 items-center justify-center rounded-xl bg-gradient-to-br shadow-lg transition-all duration-300 group-hover:scale-110 ${etapa.gradiente} ${etapa.brilho}`}
              >
                <FontAwesomeIcon icon={etapa.icone} className="text-xl text-white" />
              </div>
              <div className="mt-3 text-center">
                <span className={`block text-xs font-bold ${etapa.texto}`}>Etapa {i + 1}</span>
                <span className="block text-sm font-semibold text-slate-700 dark:text-slate-300">
                  {etapa.rotulo}
                </span>
                <span className="hidden text-xs text-slate-500 dark:text-slate-400 md:block">
                  {etapa.descricao}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

function NovoProcesso() {
  return (
    <div className="mb-8">
      <div className="mb-4 flex items-center gap-2">
        <FontAwesomeIcon icon={faPlusCircle} className="text-emerald-500" />
        <h2 className="text-lg font-bold text-slate-800 dark:text-slate-100">
          Iniciar Novo Processo
        </h2>
      </div>
      <div className="group relative overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-lg transition-all duration-300 hover:shadow-xl dark:border-slate-700 dark:bg-dark-card">
        <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/5 to-green-500/5 opacity-0 transition-opacity group-hover:opacity-100" />
        <div className="absolute left-0 top-0 h-1 w-full bg-gradient-to-r from-emerald-500 to-green-500" />
        <div className="relative flex flex-col gap-4 p-6 md:flex-row md:items-center md:justify-between">
          <div className="flex items-center gap-4">
            <div className="flex h-16 w-16 items-center justify-center rounded-xl bg-gradient-to-br from-emerald-500 to-green-600 shadow-lg shadow-emerald-500/30 transition-transform group-hover:scale-110">
              <FontAwesomeIcon icon={faFileAlt} className="text-2xl text-white" />
            </div>
            <div>
              <h3 className="text-xl font-bold text-slate-800 dark:text-slate-100">
                Criar Novo DFD
              </h3>
              <p className="text-slate-500 dark:text-slate-400">
                Documento de Formalização da Demanda — primeiro passo do processo licitatório
              </p>
            </div>
          </div>
          <a
            href="/app/dfd/novo"
            className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-xl bg-gradient-to-r from-emerald-500 to-green-600 px-6 py-3 font-semibold text-white shadow-lg shadow-emerald-500/30 transition-all hover:from-emerald-600 hover:to-green-700 hover:shadow-emerald-500/50"
          >
            <FontAwesomeIcon icon={faPlus} />
            Iniciar Processo
          </a>
        </div>
      </div>
    </div>
  )
}

function Etiqueta({ processo }: { processo: ProcessoLicitatorio }) {
  if (processo.completo) {
    return (
      <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-semibold text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400">
        <FontAwesomeIcon icon={faCheckCircle} className="mr-1" />
        Completo
      </span>
    )
  }
  if (processo.status === 'Aguardando Seleção de Preços') {
    return (
      <span className="animate-pulse rounded-full bg-orange-100 px-2 py-0.5 text-xs font-semibold text-orange-600 dark:bg-orange-900/30 dark:text-orange-400">
        <FontAwesomeIcon icon={faHandPointer} className="mr-1" />
        Selecionar Preços
      </span>
    )
  }
  if (processo.status.startsWith('Processando') || processo.status === 'Gerando ETP') {
    return (
      <span className="animate-pulse rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-600 dark:bg-amber-900/30 dark:text-amber-400">
        <FontAwesomeIcon icon={faSpinner} spin className="mr-1" />
        {processo.status}
      </span>
    )
  }
  return (
    <span className="rounded-full bg-blue-100 px-2 py-0.5 text-xs font-semibold text-blue-600 dark:bg-blue-900/30 dark:text-blue-400">
      <FontAwesomeIcon icon={faClock} className="mr-1" />
      Em Andamento
    </span>
  )
}

function CardProcesso({ processo }: { processo: ProcessoLicitatorio }) {
  const [confirmando, setConfirmando] = useState(false)
  const clienteQuery = useQueryClient()
  const { notificar } = useToast()

  const excluir = useMutation({
    mutationFn: () => excluirProcessoLicitatorio(processo.id),
    onSuccess: () => {
      void clienteQuery.invalidateQueries({ queryKey: ['processos-licitatorios'] })
      notificar('success', 'Processo excluído.')
    },
    onError: () => notificar('error', 'Erro ao excluir o processo.'),
  })

  return (
    <div className="group relative overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-lg transition-all duration-300 hover:shadow-xl dark:border-slate-700 dark:bg-dark-card">
      <div className="h-1.5 bg-slate-100 dark:bg-slate-700">
        <div
          className="h-full bg-gradient-to-r from-emerald-500 via-blue-500 to-purple-500 transition-all duration-500"
          style={{ width: `${processo.percentual}%` }}
        />
      </div>

      <div className="p-5">
        <div className="mb-4 flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            <div className="mb-1 flex flex-wrap items-center gap-2">
              <span className="rounded bg-slate-100 px-2 py-0.5 font-mono text-xs text-slate-600 dark:bg-slate-700 dark:text-slate-300">
                #{processo.id}
              </span>
              <Etiqueta processo={processo} />
              {processo.is_credenciamento && (
                <span className="rounded-full bg-violet-100 px-2 py-0.5 text-xs font-semibold text-violet-600 dark:bg-violet-900/30 dark:text-violet-400">
                  Credenciamento
                </span>
              )}
            </div>
            <h3
              className="truncate text-base font-bold text-slate-800 dark:text-slate-100"
              title={processo.objeto}
            >
              {processo.objeto}
            </h3>
            <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
              <FontAwesomeIcon icon={faCalendarAlt} className="mr-1" />
              {processo.criado_em.slice(0, 10).split('-').reverse().join('/')}
            </p>
          </div>
          <button
            onClick={() => setConfirmando(true)}
            aria-label={`Excluir processo ${processo.id}`}
            className="rounded-lg p-2 text-slate-400 transition-colors hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-900/20"
          >
            <FontAwesomeIcon icon={faTrashAlt} />
          </button>
        </div>

        {/* Trilhos por etapa */}
        <div className="mb-4 flex items-center gap-1">
          {ETAPAS.map((etapa) => {
            const feita = processo.etapas[etapa.chave]
            return (
              <div key={etapa.chave} className="flex flex-1 flex-col items-center" title={etapa.rotulo}>
                <div
                  className={`h-2 w-full rounded-full ${
                    feita ? etapa.trilho : 'bg-slate-200 dark:bg-slate-600'
                  }`}
                />
                <span
                  className={`mt-1 text-[10px] ${
                    feita ? `font-semibold ${etapa.texto}` : 'text-slate-400'
                  }`}
                >
                  {etapa.curto}
                </span>
              </div>
            )
          })}
        </div>

        <div className="space-y-2">
          {processo.proxima_acao && (
            <a
              href={processo.proxima_acao.url}
              className={`flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r px-4 py-2.5 font-semibold text-white shadow-md transition-all ${TONS_ACAO[processo.proxima_acao.tom]}`}
            >
              <FontAwesomeIcon icon={faEdit} />
              {processo.proxima_acao.rotulo}
            </a>
          )}
          {processo.acao_status && (
            <a
              href={processo.acao_status.url}
              className={`flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r px-4 py-2.5 font-semibold text-white shadow-md transition-all ${TONS_ACAO[processo.acao_status.tom]}`}
            >
              <FontAwesomeIcon icon={faSpinner} spin />
              {processo.acao_status.rotulo}
            </a>
          )}
          {processo.documentos.length > 0 && (
            <div className="flex flex-wrap gap-2 pt-1">
              {processo.documentos.map((d) => (
                <a
                  key={d.rotulo}
                  href={d.url}
                  className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-600 transition-colors hover:border-indigo-300 hover:bg-indigo-50 hover:text-indigo-700 dark:border-slate-600 dark:text-slate-300 dark:hover:bg-dark-hover"
                >
                  Ver {d.rotulo}
                </a>
              ))}
            </div>
          )}
        </div>
      </div>

      {confirmando && (
        <div className="absolute inset-0 z-20 flex flex-col items-center justify-center gap-4 bg-white/95 p-6 text-center backdrop-blur dark:bg-dark-card/95">
          <FontAwesomeIcon icon={faExclamationTriangle} className="text-3xl text-red-500" />
          <p className="text-sm text-slate-700 dark:text-slate-200">
            Excluir o processo <strong>#{processo.id}</strong> e todos os seus documentos?
          </p>
          <div className="flex gap-2">
            <button
              onClick={() => setConfirmando(false)}
              className="rounded-lg bg-slate-200 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-300 dark:bg-dark-hover dark:text-slate-200"
            >
              Cancelar
            </button>
            <button
              onClick={() => excluir.mutate()}
              disabled={excluir.isPending}
              className="rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
            >
              {excluir.isPending ? 'Excluindo…' : 'Excluir'}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

export function ProcessosPage() {
  const [busca, setBusca] = useState('')
  const [filtro, setFiltro] = useState<'all' | 'em_andamento' | 'completo'>('all')

  const consulta = useQuery({
    queryKey: ['processos-licitatorios'],
    queryFn: listarProcessosLicitatorios,
  })

  const visiveis = useMemo(() => {
    const termo = busca.trim().toLowerCase()
    return (consulta.data ?? []).filter((p) => {
      if (termo && !p.objeto.toLowerCase().includes(termo)) return false
      if (filtro === 'completo') return p.completo
      if (filtro === 'em_andamento') return !p.completo
      return true
    })
  }, [consulta.data, busca, filtro])

  if (consulta.isLoading) {
    return <p className="p-8 text-center text-slate-500">Carregando processos…</p>
  }

  const processos = consulta.data ?? []

  return (
    <div className="mx-auto max-w-7xl">
      <Cabecalho processos={processos} />
      <FluxoDoProcesso />
      <NovoProcesso />

      <div className="mb-8">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <FontAwesomeIcon icon={faFolderOpen} className="text-indigo-500" />
            <h2 className="text-lg font-bold text-slate-800 dark:text-slate-100">Meus Processos</h2>
            <span className="rounded-full bg-indigo-100 px-2 py-0.5 text-xs font-semibold text-indigo-600 dark:bg-indigo-900/30 dark:text-indigo-400">
              {processos.length}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <div className="relative">
              <input
                value={busca}
                onChange={(e) => setBusca(e.target.value)}
                placeholder="Buscar processo..."
                aria-label="Buscar processo"
                className="w-48 rounded-lg border border-slate-200 py-2 pl-9 pr-4 text-sm transition-all focus:border-transparent focus:ring-2 focus:ring-indigo-500 dark:border-slate-600 dark:bg-dark-bg dark:text-slate-100 md:w-64"
              />
              <FontAwesomeIcon
                icon={faSearch}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
              />
            </div>
            <select
              value={filtro}
              onChange={(e) => setFiltro(e.target.value as typeof filtro)}
              aria-label="Filtrar por situação"
              className="rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-transparent focus:ring-2 focus:ring-indigo-500 dark:border-slate-600 dark:bg-dark-bg dark:text-slate-100"
            >
              <option value="all">Todos</option>
              <option value="em_andamento">Em Andamento</option>
              <option value="completo">Completos</option>
            </select>
          </div>
        </div>

        {visiveis.length > 0 ? (
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3">
            {visiveis.map((p) => (
              <CardProcesso key={p.id} processo={p} />
            ))}
          </div>
        ) : (
          <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-12 text-center dark:border-slate-600 dark:bg-dark-card">
            <FontAwesomeIcon icon={faFolderOpen} className="mb-3 text-4xl text-slate-300" />
            <p className="text-slate-500 dark:text-slate-400">
              {processos.length === 0
                ? 'Nenhum processo criado ainda. Comece criando um DFD.'
                : 'Nenhum processo corresponde ao filtro.'}
            </p>
          </div>
        )}
      </div>
    </div>
  )
}
